import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import './Marketplace.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface Product {
  id: number;
  name: string;
  description: string;
  price: string | number;
  image: string;
  stripe_product_id: string;
  stripe_price_id: string;
  category: string;
  script_string: string;
  is_recurring: boolean;
  interval?: 'month' | 'year';  // subscriptions only, from the API
  is_service: boolean;
  active: boolean;
  display_order: number;
  sale_price_id: string | null;
  sale_price: string | number | null;
  sale_ends_at: string | null;
  created_at: string;
  updated_at: string;
}

// A product is on sale when it has a sale Price and the sale hasn't expired.
// Mirrors the same check the checkout endpoint makes server-side.
const saleActive = (product: Product): boolean =>
  !!product.sale_price_id && product.sale_price != null &&
  (!product.sale_ends_at || new Date(product.sale_ends_at) > new Date());

const amount = (value: string | number): string =>
  (typeof value === 'number' ? value : parseFloat(value)).toFixed(2);

const Marketplace: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState<number | null>(null);
  const [hasActiveSub, setHasActiveSub] = useState<boolean>(false);

  // Pre-purchase modal state
  const [showPrePurchaseModal, setShowPrePurchaseModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [customerNotes, setCustomerNotes] = useState('');
  const [quantity, setQuantity] = useState(1);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/api/products?active=true`);
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API_BASE]);

  // Prefetch subscription state so we can short-circuit subscription clicks
  // straight to /settings rather than dead-ending in Stripe Checkout's
  // "manage subscription" page.
  useEffect(() => {
    if (!user) {
      setHasActiveSub(false);
      return;
    }
    axios.get(`${API_BASE}/api/stripe/plan-options`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    }).then(res => {
      setHasActiveSub(!!res.data?.hasActiveSub);
    }).catch(() => {
      setHasActiveSub(false);
    });
  }, [API_BASE, user]);

  // Get unique categories from products, capitalize them
  const categories = ['All', ...Array.from(new Set(products.map(p => {
    // Capitalize first letter of each word
    return p.category.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  })))];

  const filteredProducts = selectedCategory === 'All'
    ? products
    : products.filter(p => {
        const normalizedCategory = p.category.split(' ').map(word =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
        return normalizedCategory === selectedCategory;
      });

  // Check if product is a service item (requires notes)
  const isServiceItem = (product: Product): boolean => {
    return product.is_service || !product.script_string || product.script_string.trim() === '';
  };

  // Open pre-purchase modal for all non-recurring items (allows quantity selection)
  const handlePurchase = (product: Product) => {
    if (!user) {
      alert('Please log in to make purchases');
      return;
    }

    // For all non-recurring items, show the pre-purchase modal (for quantity and notes)
    if (!product.is_recurring) {
      setSelectedProduct(product);
      setCustomerNotes('');
      setQuantity(1);
      setShowPrePurchaseModal(true);
    } else if (hasActiveSub) {
      // Existing subscriber: Stripe Checkout would dead-end them on a
      // "manage subscription" page with no upgrade path. Send them to
      // Settings, where the tier-change UI lives.
      alert('You already have an active subscription. Use Settings to change your tier.');
      navigate('/settings');
    } else {
      // For subscriptions, proceed directly (quantity doesn't apply)
      proceedToCheckout(product, '', 1);
    }
  };

  // Close modal and reset state
  const closePrePurchaseModal = () => {
    setShowPrePurchaseModal(false);
    setSelectedProduct(null);
    setCustomerNotes('');
    setQuantity(1);
  };

  // Handle the actual checkout process
  const proceedToCheckout = async (product: Product, notes: string, qty: number = 1) => {
    setPurchaseLoading(product.id);
    closePrePurchaseModal();

    try {
      if (product.is_recurring) {
        // Handle subscription purchase
        const response = await axios.post(`${API_BASE}/api/stripe/create-checkout-session`, {
          priceId: product.stripe_price_id
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        // Backend bounces existing subscribers to Settings rather than
        // creating a new Checkout session that dead-ends them.
        if (response.data.redirect) {
          if (response.data.reason === 'has_active_subscription') {
            alert('You already have an active subscription. Use Settings to change your tier.');
          }
          navigate(response.data.redirect);
        } else if (response.data.sessionUrl) {
          window.location.href = response.data.sessionUrl;
        }
      } else {
        // Handle one-time purchase using the checkout endpoint
        const response = await axios.post(`${API_BASE}/api/checkout/create-session`, {
          product_id: product.id,
          quantity: qty,
          customer_notes: notes.trim() || undefined
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error: any) {
      console.error('Purchase failed:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Your session has expired. Please log in again to complete your purchase.');
        // Clear the invalid token
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      } else {
        alert('Purchase failed. Please try again.');
      }
    } finally {
      setPurchaseLoading(null);
    }
  };

  const getCategoryColor = (category: string) => {
    const normalized = category.toLowerCase();
    switch (normalized) {
      case 'subscriptions': return '#6f42c1';
      case 'consumables': return '#007bff';
      case 'tickets': return '#fd7e14';
      case 'real estate': return '#28a745';
      case 'gang services': return '#dc3545';
      case 'miscellaneous': return '#6c757d';
      default: return '#6c757d';
    }
  };

  return (
    <div className="marketplace-page">
      {!user && (
        <div className="guest-notice">
          <p>🔒 Log in to purchase items and unlock the full marketplace experience!</p>
        </div>
      )}

      <div className="marketplace-filters">
        <div className="category-filters">
          {categories.map(category => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div style={{ fontSize: '18px' }}>Loading products...</div>
        </div>
      )}

      {!loading && filteredProducts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📦</div>
          <div style={{ fontSize: '18px' }}>No products available in this category.</div>
        </div>
      )}

      {!loading && filteredProducts.length > 0 && (
        <div className="marketplace-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="marketplace-item">
              <div className="item-image">
                {product.image.startsWith('http') ? (
                  <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span className="item-emoji">{product.image}</span>
                )}
                <div
                  className="item-rarity"
                  style={{ backgroundColor: getCategoryColor(product.category) }}
                >
                  {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                </div>
              </div>

              <div className="item-content">
                <h3 className="item-name">{product.name}</h3>
                <p className="item-description">{product.description || 'No description available'}</p>
                <div className="item-category">{product.category}</div>
              </div>

              <div className="item-footer">
                <div className="item-price">
                  {saleActive(product) && (
                    <span className="list-price">${amount(product.price)}</span>
                  )}
                  <span className={saleActive(product) ? 'sale-price' : undefined}>
                    ${amount(saleActive(product) ? product.sale_price! : product.price)}
                    {product.is_recurring && (product.interval === 'year' ? '/year' : '/month')}
                  </span>
                </div>

                <button
                  className="purchase-btn"
                  onClick={() => handlePurchase(product)}
                  disabled={!user || purchaseLoading === product.id}
                >
                  {purchaseLoading === product.id ? (
                    'Processing...'
                  ) : !user ? (
                    'Login Required'
                  ) : product.is_recurring ? (
                    'Subscribe'
                  ) : (
                    'Purchase'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {user && !loading && (
        <div className="marketplace-info">
          <div className="info-card">
            <h3>🛡️ Secure Payments</h3>
            <p>All transactions are processed securely through Stripe. Your payment information is never stored on our servers.</p>
          </div>
          <div className="info-card">
            <h3>⚡ Instant Delivery</h3>
            <p>Digital items are delivered instantly to your account upon successful payment.</p>
          </div>
          <div className="info-card">
            <h3>🎮 Enhanced Gameplay</h3>
            <p>Premium items and subscriptions unlock exclusive content and enhanced gaming experiences.</p>
          </div>
        </div>
      )}

      {/* Pre-Purchase Modal for Service Items */}
      {showPrePurchaseModal && selectedProduct && (
        <div className="pre-purchase-overlay" onClick={closePrePurchaseModal}>
          <div className="pre-purchase-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closePrePurchaseModal}>×</button>

            <div className="modal-product-header">
              <div className="modal-product-image">
                {selectedProduct.image.startsWith('http') ? (
                  <img src={selectedProduct.image} alt={selectedProduct.name} />
                ) : (
                  <span className="modal-emoji">{selectedProduct.image}</span>
                )}
              </div>
              <div className="modal-product-info">
                <h2>{selectedProduct.name}</h2>
                <p className="modal-product-description">{selectedProduct.description}</p>
                <div className="modal-product-price">
                  {(() => {
                    const onSale = saleActive(selectedProduct);
                    const unit = parseFloat(amount(onSale ? selectedProduct.sale_price! : selectedProduct.price));
                    return (
                      <>
                        {onSale && (
                          <span className="list-price">
                            ${(parseFloat(amount(selectedProduct.price)) * quantity).toFixed(2)}
                          </span>
                        )}
                        <span className={onSale ? 'sale-price' : undefined}>
                          ${(unit * quantity).toFixed(2)}
                        </span>
                        {quantity > 1 && (
                          <span className="unit-price">(${unit.toFixed(2)} each)</span>
                        )}
                      </>
                    );
                  })()}
                </div>
                {saleActive(selectedProduct) && (
                  <div className="sale-note">
                    On sale. Platinum members get a further 10% off at checkout.
                  </div>
                )}
              </div>
            </div>

            <div className="modal-quantity-section">
              <label htmlFor="quantity">Quantity</label>
              <div className="quantity-selector">
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= 99) {
                      setQuantity(val);
                    }
                  }}
                  min={1}
                  max={99}
                />
                <button
                  type="button"
                  className="quantity-btn"
                  onClick={() => setQuantity(Math.min(99, quantity + 1))}
                  disabled={quantity >= 99}
                >
                  +
                </button>
              </div>
            </div>

            <div className="modal-notes-section">
              <label htmlFor="customer-notes">
                Customization Notes <span className="optional-label">(optional)</span>
              </label>
              <p className="notes-description">
                Please provide any custom information we need to fulfill your order
                (e.g., character name, specific requests, preferences).
              </p>
              <textarea
                id="customer-notes"
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value.slice(0, 255))}
                placeholder="Enter any special instructions or customization details..."
                rows={4}
                maxLength={255}
              />
              <div className="char-counter">{customerNotes.length}/255</div>
            </div>

            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={closePrePurchaseModal}
              >
                Cancel
              </button>
              <button
                className="modal-checkout-btn"
                onClick={() => proceedToCheckout(selectedProduct, customerNotes, quantity)}
                disabled={purchaseLoading === selectedProduct.id}
              >
                {purchaseLoading === selectedProduct.id ? 'Processing...' : 'Proceed to Checkout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketplace;
